import { ICategoryInfo, IDeepLinkInfo, IUserMentionData } from '@gem/models/commonModel';
import { IBotUserDisplayData, IUserDisplayData } from '@gem/models/userModel';

import { GroupTypeAddUsersEnum } from 'APP/model/search/searchModel.types';

import { IGroupCounterView } from '../counter/counterModel.types';

export enum GroupUserRole {
  Owner = 'OWNER',
  Admin = 'ADMIN',
  Member = 'MEMBER',
  Guest = 'GUEST',
  Banned = 'BANNED',
}

export enum GroupType {
  My = 'group.my',
  P2P = 'group.private.single',
  Open = 'group.open',
  Closed = 'group.private',
  ChannelOpen = 'group.public.open',
  ChannelClosed = 'group.public.closed',
  MySavedMessages = 'group.my.saved_messages',
  SavedMessagesFake = 'fakeSavedMessagesGroup',
  Thread = 'group.thread',
  Schedule = 'group.schedule',
  System = 'group.system',
  SecretGroup = 'group.secret',
  ServiceMessages = 'group.serviceMessages',
}

export interface ISupportedMediaTypes {
  canSendImage: boolean;
  canSendVideo: boolean;
  canSendSticker: boolean;
  canSendAudio: boolean;
  canSendVoice: boolean;
  canSendFile: boolean;
  canSendLink: boolean;
  cansSendCircleVideo: boolean;
}

export interface IMemberCustomPermissions {
  canInitCall: boolean;
  canInitConference: boolean;
  canInviteUsers: boolean;
  canLeaveComment: boolean;
  canPinMessage: boolean;
  canSendMessages: boolean;
  canDisplayMembers: boolean;
  supportedMediaTypes: ISupportedMediaTypes;
  updatedAt: string;
}

export interface IMemberCustomPermissionsRequest {
  groupId: string;
  canInviteUsers: boolean;
  canInitCall: boolean;
  canInitConference: boolean;
  canPinMessage: boolean;
  canLeaveComment: boolean;
  canSendMessages: boolean;
  canDisplayMembers: boolean;
  canInitSystemMessage?: boolean;
  supportedMediaTypes: ISupportedMediaTypes;
}

export interface IDraftInput {
  groupId: string;
  text?: string;
  updateTs: number;
}

export interface IGroupSettings {
  systemMessagesEnabled: boolean;
}

export interface ISetGroupSettings {
  groupId: string;
  systemMessagesEnabled?: boolean;
}

export interface IGroup {
  avatarUrl?: string;
  categoryId?: string;
  categoryInfo?: ICategoryInfo;
  deepLinkInfo: IDeepLinkInfo;
  deleted: boolean;
  description?: string;
  descriptionMentions: IUserMentionData[];
  donationUrl?: string;
  id: string;
  memberCustomPermissions: IMemberCustomPermissions;
  name: string;
  pinnedMessageIds: string[];
  spaceId?: string;
  type: string;
  usersCount: number;
  verified: boolean;
  settings?: IGroupSettings;
  obsceneFilterEnabled: boolean;
  keywordFilters: number;
  ttlPeriodMs: number;
  activeChatAccessRequestCount: number;
}

export interface IPersonalizedGroup {
  botOpponent?: IBotUserDisplayData;
  draftInput?: IDraftInput;
  groupResponse: IGroup;
  muted: boolean;
  opponent?: IUserDisplayData;
  pinOrder: number;
  unread: boolean;
  showContent?: boolean;
  userGroupRole: GroupUserRole;
}

export interface IParticipan {
  userId: string;
  role: GroupUserRole;
}

export type IGroupData = IGroup & Partial<Omit<IPersonalizedGroup, 'groupResponse'>>;

export interface ICollectionResponsePersonalizedGroup {
  items: IPersonalizedGroup[];
  userBannedInGroups: Array<string>;
}

export interface IGroupWithBlockStatusResponse {
  personalizedGroup: IPersonalizedGroup;
  usersFailedToInvite: Array<string>;
  groupCounter: IGroupCounterView;
}

export interface IPinMessagesRequest {
  groupId: string;
  messageIds: string[];
}

export interface IGroupIdRequest {
  groupId: string;
}

export interface ISearchUsersInGroupResponse {
  cursor: string | null;
  participants: IParticipan[];
}

export type IGetGroupUserRolesRequest = {
  groupId: string;
  cursor?: string;
  limit?: number;
};

export interface IGroupUserRolesResponse {
  cursor?: string;
  participants: IParticipan[];
}

export type IChangeOwnershipRequest = {
  groupId: string;
  newOwnerId: string;
};

export type IBanUsersForGroupRequest = {
  groupId: string;
  usersToBan: Array<string>;
  deleteAllMessages?: boolean;
};

export type IGroupParticipantChangeRequest = {
  groupId: string;
  userIds: Array<string>;
};

export type ISetObsceneFilterRequest = {
  groupId: string;
  enabled: boolean;
};

export type ISetKeywordFiltersRequest = {
  groupId: string;
  keywords: Array<string>;
};

export type IGetBannedUsersForGroupRequest = {
  groupId: string;
};

export type IGetBannedUsersForGroupResponse = {
  bannedUserIds: Array<string>;
};

export type IUnbanUsersForGroupRequest = {
  groupId: string;
  usersToUnban: Array<string>;
};

export type IGroupAdvertisementSettingsResponse = {
  groupId: string;
  adsEnabled: boolean;
};

export type ISetGroupAdvertisementSettingsRequest = {
  groupId: string;
  adsEnabled: boolean;
};

export type IDeleteGroupDeepLinkRequest = {
  groupId: string;
};

export type IDeepLinkExistsRequest = {
  deepLinkAlias?: string;
};

export interface ICreateGroupRequest {
  name?: string | null;
  description?: string | null;
  type: GroupType;
  members?: Array<string>;
  admins?: Array<string>;
  categoryId?: string;
  tags?: Array<string>;
  deepLinkAlias?: string;
  avatarUrl?: string | null;
  donationUrl?: string;
  spaceId?: string | null;
}

export type IGetFriendsNotInvitedToGroupRequest = {
  groupId: string;
};

export type IGetFriendsNotInvitedToGroupResponse = {
  usersDisplayData: Array<IUserDisplayData>;
};

export type IPinGroupRequest = {
  groupId: string;
};

export interface IUpdateGroupInfoRequest {
  groupId: string;
  name: string;
  description?: string;
  avatarUrl?: string;
  categoryId?: string;
  deepLinkAlias?: string;
  donationUrl?: string;
  groupType: GroupType;
}

export type IGetOrCreateThreadRequest = {
  groupId: string;
  messageId: string;
};

export type IGetThreadInfoByIdRequest = {
  threadId: string;
};

export type IThreadInfoResponse = {
  groupId: string;
  messageId: string;
  threadId: string;
  groupCounter: IGroupCounterView;
};

export type IGetThreadsInfoRequest = {
  groupId: string;
  messageIds: Array<string>;
};

export type IThreadsInfoResponse = {
  items: Array<{
    groupId: string;
    messageId: string;
    threadId: string;
    groupCounter: IGroupCounterView;
  }>;
};

export type IClearGroupMessageRequest = {
  groupId: string;
};

export interface IGetUsersAddToGroupRequest {
  groupId?: string;
  groupType?: GroupTypeAddUsersEnum;
  cursor?: string;
  limit: number;
  offset?: string;
}

export interface IGetUsersAddToGroupResponse {
  cursor?: string;
  users: IUserDisplayData[];
  hasMore: boolean;
}

export interface IUserTypingMessageEvent {
  groupId: string;
  userId: string;
  userName: string;
}

export interface IMessageSentEvent {
  groupId: string;
  userId: string;
}

export interface ISetChatMessagesHistoryTtlRequest {
  groupId: string;
  ttlPeriodMs: number;
}
